import { useState } from 'react';
import { Loader2, RefreshCw, Settings2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const AREA_LABELS = {
  'app-core': 'App Core',
  'financial-feeds': 'Financial Feeds',
  'banking': 'Banking',
};

const AREA_ORDER = ['app-core', 'financial-feeds', 'banking'];

function PresencePill({ present, required }) {
  if (present) {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-[10px]">
        <CheckCircle2 className="h-3 w-3 mr-0.5" />
        present
      </Badge>
    );
  }
  if (required) {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px]">
        <XCircle className="h-3 w-3 mr-0.5" />
        MISSING
      </Badge>
    );
  }
  return (
    <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 text-[10px]">
      <AlertTriangle className="h-3 w-3 mr-0.5" />
      missing
    </Badge>
  );
}

function VarRow({ entry }) {
  return (
    <div className="flex items-start gap-3 px-3 py-1.5">
      <div className="pt-0.5 shrink-0">
        <PresencePill present={entry.present} required={entry.required_in_production} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-medium">{entry.name}</span>
          {entry.required_in_production && (
            <Badge variant="outline" className="text-[9px] px-1 py-0">required</Badge>
          )}
          {entry.hint && (
            <span className="text-[10px] text-muted-foreground font-mono">{entry.hint}</span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">{entry.gates}</div>
      </div>
    </div>
  );
}

export function ConfigReadinessCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.configReadiness();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Config Readiness
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={runCheck} disabled={loading} size="sm">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : data ? (
            <RefreshCw className="h-4 w-4 mr-2" />
          ) : null}
          {data ? 'Refresh' : 'Check readiness'}
        </Button>

        {error && (
          <div className="text-sm text-red-500">{error}</div>
        )}

        {data && (
          <>
            {/* Summary */}
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">
                {data.present_count}/{data.total} present
              </span>
              {data.status === 'PASS' ? (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  ALL REQUIRED SET
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                  <XCircle className="h-3 w-3 mr-1" />
                  {data.fails.length} REQUIRED MISSING
                </Badge>
              )}
            </div>

            {/* FAILs block (required-but-missing, surfaced first) */}
            {data.fails.length > 0 && (
              <div className="space-y-1">
                <div className="text-sm font-medium text-red-700">
                  Required but missing ({data.fails.length})
                </div>
                <div className="border rounded-md bg-red-50 p-2 space-y-1">
                  {data.fails.map((f) => (
                    <div key={f.name} className="text-xs">
                      <span className="font-mono font-medium text-red-800">{f.name}</span>
                      <span className="text-red-700 ml-2">{f.gates}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Grouped table */}
            {AREA_ORDER.map((area) => {
              const vars = data.groups[area];
              if (!vars || vars.length === 0) return null;
              return (
                <div key={area} className="space-y-1">
                  <div className="text-sm font-semibold">{AREA_LABELS[area]}</div>
                  <div className="border rounded-md divide-y">
                    {vars.map((entry) => (
                      <VarRow key={entry.name} entry={entry} />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </CardContent>
    </Card>
  );
}
